import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Search, Star, MapPin, Briefcase, ArrowLeft, Plus, FileText, Sparkles, CalendarDays, Crown } from "lucide-react";

const MODE_LABEL = { domicilio: "A domicilio", presso: "In sede", online: "Online" };
const PRICE_LABEL = { orario: "Prezzo orario", fisso: "Prezzo fisso", preventivo: "Preventivo", gratuita: "Consulenza gratuita" };

export default function ProList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ professionals: [], categories: [], zones: [], service_modes: [] });
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [zone, setZone] = useState("");
  const [mode, setMode] = useState("");

  const load = async () => {
    const params = {};
    if (q) params.q = q; if (category) params.category = category; if (zone) params.zone = zone; if (mode) params.service_mode = mode;
    const { data } = await api.get("/public/professionals", { params });
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, zone, mode]);

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white">
      <header className="sticky top-0 z-10 bg-[#0A0A0C]/90 backdrop-blur-xl border-b border-white/10 px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <button data-testid="pro-back" onClick={() => navigate("/esplora")} className="text-zinc-400 hover:text-white"><ArrowLeft className="h-5 w-5" /></button>
          <h1 className="font-['Unbounded'] font-extrabold text-lg tracking-tight flex-1">Professionisti</h1>
          {(!user || user.role === "customer") && <Button data-testid="my-bookings-link" onClick={() => navigate("/app/bookings")} size="sm" variant="ghost" className="text-zinc-300 hover:text-white"><CalendarDays className="h-4 w-4 mr-1" /> Prenotazioni</Button>}
          {(!user || user.role === "customer") && <Button data-testid="my-quotes-link" onClick={() => navigate("/app/quotes")} size="sm" variant="ghost" className="text-zinc-300 hover:text-white"><FileText className="h-4 w-4 mr-1" /> Preventivi</Button>}
          <Button data-testid="pro-register-cta" onClick={() => navigate("/professionisti/registrazione")} size="sm" className="bg-[#00E676] text-black rounded-full font-semibold"><Plus className="h-4 w-4 mr-1" /> Iscriviti</Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-5 space-y-4">
        <button data-testid="publish-quote-banner" onClick={() => navigate("/app/quotes", { state: { openNew: true } })} className="w-full text-left rounded-2xl border border-[#00E676]/30 bg-gradient-to-r from-[#00E676]/10 to-transparent p-4 flex items-center gap-3 hover:border-[#00E676]/60 transition-colors">
          <div className="h-11 w-11 rounded-xl bg-[#00E676]/15 flex items-center justify-center shrink-0"><Sparkles className="h-5 w-5 text-[#00E676]" /></div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold">Non sai chi scegliere? Pubblica una richiesta</div>
            <div className="text-xs text-zinc-400">Descrivi ciò che ti serve e ricevi preventivi dai professionisti della zona.</div>
          </div>
          <Plus className="h-5 w-5 text-[#00E676] shrink-0" />
        </button>

        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input data-testid="pro-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca un professionista, competenza…" className="pl-10 h-11 rounded-full bg-[#141416] border-white/10" />
        </form>

        <div className="flex gap-2 flex-wrap">
          <select data-testid="filter-category" value={category} onChange={(e) => setCategory(e.target.value)} className="bg-[#141416] border border-white/10 rounded-full px-3 py-2 text-sm">
            <option value="">Tutte le categorie</option>
            {data.categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="filter-zone" value={zone} onChange={(e) => setZone(e.target.value)} className="bg-[#141416] border border-white/10 rounded-full px-3 py-2 text-sm">
            <option value="">Tutta l'area</option>
            {data.zones.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
          <select data-testid="filter-mode" value={mode} onChange={(e) => setMode(e.target.value)} className="bg-[#141416] border border-white/10 rounded-full px-3 py-2 text-sm">
            <option value="">Tutte le modalità</option>
            {data.service_modes.map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
          </select>
        </div>

        {data.professionals.length === 0 ? (
          <div data-testid="pro-empty" className="text-center py-20 text-zinc-500">Nessun professionista trovato. Sii il primo a iscriverti!</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {data.professionals.map((p) => (
              <button key={p.id} data-testid={`pro-card-${p.id}`} onClick={() => navigate(`/professionisti/${p.id}`)}
                className="text-left bg-[#141416] border border-white/10 rounded-2xl p-4 hover:border-[#00E676]/40 transition-colors">
                <div className="flex gap-3">
                  <div className="h-16 w-16 shrink-0 rounded-xl overflow-hidden bg-[#1C1C1F] flex items-center justify-center border border-white/10">
                    {p.photo ? <img src={p.photo} alt={p.name} className="h-full w-full object-cover" /> : <Briefcase className="h-6 w-6 text-zinc-500" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{p.name}</div>
                    <div className="text-xs text-[#00E676] truncate">{(p.categories || []).join(" · ")}</div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-zinc-400">
                      {p.rating > 0 && <span className="flex items-center gap-0.5 text-amber-300"><Star className="h-3 w-3 fill-amber-300" /> {p.rating}</span>}
                      <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" /> {p.area_mode === "canavese" ? "Tutto il Canavese" : p.zone}</span>
                    </div>
                  </div>
                </div>
                {p.headline && <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{p.headline}</p>}
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {(p.service_modes || []).map((m) => <span key={m} className="text-[10px] rounded-full bg-white/10 px-2 py-0.5">{MODE_LABEL[m]}</span>)}
                  {p.pricing_mode && <span className="text-[10px] rounded-full bg-[#00E676]/15 text-[#00E676] px-2 py-0.5">{PRICE_LABEL[p.pricing_mode]}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
        {!user && <div className="text-center text-xs text-zinc-500 pt-4">Accedi per contattare un professionista o lasciare una recensione.</div>}
      </div>
    </div>
  );
}
